import { ref, watch } from 'vue';
import { currentUser, getAppSettings, saveAppSettings } from '@/services/bluesky';

const STORAGE_KEY = 'app_config';

function loadLocalConfig() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
}

const saved = loadLocalConfig();

export const showTimeline = ref<boolean>(saved.showTimeline ?? false);
export const appTheme = ref<'dark' | 'light'>(saved.appTheme ?? 'dark');

export const vuetifyTheme = () => appTheme.value === 'dark' ? 'darkGoldenTheme' : 'lightGoldenTheme';

// Persist to localStorage
function persistLocal() {
  localStorage.setItem(STORAGE_KEY, JSON.stringify({
    showTimeline: showTimeline.value,
    appTheme: appTheme.value,
  }));
}

// Debounced PDS save
let saveTimer: ReturnType<typeof setTimeout> | null = null;
function schedulePdsSave() {
  if (!currentUser.value) return;
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(async () => {
    try {
      await saveAppSettings({ showTimeline: showTimeline.value, appTheme: appTheme.value });
    } catch (e) {
      console.error('[AppConfig] Failed to save to PDS:', e);
    }
  }, 800);
}

watch(showTimeline, () => { persistLocal(); schedulePdsSave(); });
watch(appTheme, () => { persistLocal(); schedulePdsSave(); });

// Load from PDS on login
watch(currentUser, async (user, prev) => {
  if (user && !prev) {
    try {
      const remote = await getAppSettings();
      if (remote) {
        showTimeline.value = remote.showTimeline ?? false;
        appTheme.value = remote.appTheme ?? 'dark';
        persistLocal();
      }
    } catch (e) {
      console.error('[AppConfig] Failed to load from PDS:', e);
    }
  }
});

export const loadAppConfigFromPds = async () => {
  try {
    const remote = await getAppSettings();
    if (remote) {
      showTimeline.value = remote.showTimeline ?? false;
      appTheme.value = remote.appTheme ?? 'dark';
      persistLocal();
    }
  } catch (e) {
    console.error('[AppConfig] Failed to load from PDS:', e);
  }
};
